import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  runTransaction,
  Firestore,
  DocumentData,
  QuerySnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import {
  Room,
  Table,
  WaitlistEntry,
  UserProfile,
  CreateRoomData,
  UpdateRoomData,
  CreateTableData,
  UpdateTableData,
  CreateWaitlistEntryData,
  UpdateWaitlistEntryData,
  CreateUserProfileData,
  UpdateUserProfileData,
} from '../types';

// Generic helper for creating documents with timestamps
const createWithTimestamps = async <T extends Record<string, any>>(
  collectionRef: any,
  data: T
) => {
  return addDoc(collectionRef, {
    ...data,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
};

const updateWithTimestamp = async <T extends Record<string, any>>(
  docRef: any,
  data: T
) => {
  // Filter out undefined values
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  );
  
  return updateDoc(docRef, {
    ...filteredData,
    updated_at: serverTimestamp(),
  } as any);
};

// Room operations
export const getRooms = async (db: Firestore): Promise<Room[]> => {
  const roomsRef = collection(db, 'rooms');
  const snapshot = await getDocs(roomsRef);
  return snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as Room));
};

export const getRoom = async (db: Firestore, roomId: string): Promise<Room | null> => {
  const roomRef = doc(db, 'rooms', roomId);
  const snapshot = await getDoc(roomRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as Room;
};

export const createRoom = async (db: Firestore, data: CreateRoomData): Promise<string> => {
  const roomsRef = collection(db, 'rooms');
  const docRef = await createWithTimestamps(roomsRef, data);
  return docRef.id;
};

export const updateRoom = async (
  db: Firestore,
  roomId: string,
  data: UpdateRoomData
): Promise<void> => {
  const roomRef = doc(db, 'rooms', roomId);
  await updateWithTimestamp(roomRef, data);
};

export const deleteRoom = async (db: Firestore, roomId: string): Promise<void> => {
  const roomRef = doc(db, 'rooms', roomId);
  await deleteDoc(roomRef);
};

// Table operations
export const getTables = async (db: Firestore, roomId: string): Promise<Table[]> => {
  const tablesRef = collection(db, 'rooms', roomId, 'tables');
  const snapshot = await getDocs(tablesRef);
  return snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as Table));
};

export const getTable = async (db: Firestore, roomId: string, tableId: string): Promise<Table | null> => {
  const tableRef = doc(db, 'rooms', roomId, 'tables', tableId);
  const snapshot = await getDoc(tableRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as Table;
};

export const createTable = async (
  db: Firestore,
  roomId: string,
  data: CreateTableData
): Promise<string> => {
  const tablesRef = collection(db, 'rooms', roomId, 'tables');
  const docRef = await createWithTimestamps(tablesRef, {
    ...data,
    status: data.status ?? 'Open',
  });
  return docRef.id;
};

export const updateTable = async (
  db: Firestore,
  roomId: string,
  tableId: string,
  data: UpdateTableData
): Promise<void> => {
  const tableRef = doc(db, 'rooms', roomId, 'tables', tableId);
  await updateWithTimestamp(tableRef, data);
};

export const deleteTable = async (db: Firestore, roomId: string, tableId: string): Promise<void> => {
  // First, get all waitlist entries for this table
  const waitlistRef = collection(db, 'rooms', roomId, 'tables', tableId, 'waitlist');
  const waitlistSnapshot = await getDocs(waitlistRef);
  
  // Delete all waitlist entries in a batch
  if (!waitlistSnapshot.empty) {
    const batch = writeBatch(db);
    waitlistSnapshot.docs.forEach((waitlistDoc) => {
      batch.delete(waitlistDoc.ref);
    });
    await batch.commit();
  }
  
  // Finally, delete the table document
  const tableRef = doc(db, 'rooms', roomId, 'tables', tableId);
  await deleteDoc(tableRef);
};

// Waitlist operations
export const getWaitlist = async (
  db: Firestore,
  roomId: string,
  tableId: string
): Promise<WaitlistEntry[]> => {
  const waitlistRef = collection(db, 'rooms', roomId, 'tables', tableId, 'waitlist');
  
  try {
    // Try to query by position first
    const q = query(waitlistRef, orderBy('position', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc: any) => {
      const data = doc.data();
      // Ensure position field exists, default to 0 if missing
      if (data.position === undefined) {
        data.position = 0;
      }
      return { id: doc.id, ...data } as WaitlistEntry;
    });
  } catch (error) {
    // Fallback to added_at ordering if position query fails
    const q = query(waitlistRef, orderBy('added_at', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc: any) => {
      const data = doc.data();
      // Ensure position field exists, default to 0 if missing
      if (data.position === undefined) {
        data.position = 0;
      }
      return { id: doc.id, ...data } as WaitlistEntry;
    });
  }
};

export const addWaitlistEntry = async (
  db: Firestore,
  roomId: string,
  tableId: string,
  data: CreateWaitlistEntryData
): Promise<string> => {
  return await runTransaction(db, async (transaction) => {
    const waitlistRef = collection(db, 'rooms', roomId, 'tables', tableId, 'waitlist');
    
    // Check for duplicate user_id entries if user_id is provided
    if (data.user_id) {
      const duplicateQuery = query(
        waitlistRef, 
        where('user_id', '==', data.user_id), 
        where('status', '==', 'waiting')
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);
      
      if (!duplicateSnapshot.empty) {
        throw new Error('User is already on the waitlist');
      }
    }
    
    // Get all current waiting entries to calculate next position
    const waitingQuery = query(waitlistRef, where('status', '==', 'waiting'), orderBy('position', 'asc'));
    const waitingSnapshot = await getDocs(waitingQuery);
    
    // Calculate next position (max position + 1, or 1 if first player)
    const maxPosition = waitingSnapshot.docs.reduce((max, doc) => {
      const position = doc.data().position || 0;
      return Math.max(max, position);
    }, 0);
    const nextPosition = maxPosition + 1;
    
    // Add the new entry with calculated position
    const docRef = doc(waitlistRef);
    transaction.set(docRef, {
      ...data,
      status: 'waiting',
      position: nextPosition,
      added_at: serverTimestamp(),
    });
    
    return docRef.id;
  });
};

export const updateWaitlistEntry = async (
  db: Firestore,
  roomId: string,
  tableId: string,
  waitlistId: string,
  data: UpdateWaitlistEntryData
): Promise<void> => {
  const waitlistRef = doc(db, 'rooms', roomId, 'tables', tableId, 'waitlist', waitlistId);
  await updateWithTimestamp(waitlistRef, data);
};

export const removeWaitlistEntry = async (
  db: Firestore,
  roomId: string,
  tableId: string,
  waitlistId: string
): Promise<void> => {
  const waitlistRef = doc(db, 'rooms', roomId, 'tables', tableId, 'waitlist', waitlistId);
  await deleteDoc(waitlistRef);
};

export const updateWaitlistPosition = async (
  db: Firestore,
  roomId: string,
  tableId: string,
  entryId: string,
  newPosition: number
): Promise<void> => {
  return await runTransaction(db, async (transaction) => {
    const waitlistRef = collection(db, 'rooms', roomId, 'tables', tableId, 'waitlist');
    
    // Get all waiting entries ordered by current position
    const waitingQuery = query(waitlistRef, where('status', '==', 'waiting'), orderBy('position', 'asc'));
    const waitingSnapshot = await getDocs(waitingQuery);
    const waitingEntries = waitingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as WaitlistEntry));
    
    // Validate new position
    if (newPosition < 1 || newPosition > waitingEntries.length) {
      throw new Error(`Invalid position: ${newPosition}. Must be between 1 and ${waitingEntries.length}`);
    }
    
    // Find the entry being moved
    const entryToMove = waitingEntries.find(entry => entry.id === entryId);
    if (!entryToMove) {
      throw new Error('Waitlist entry not found');
    }
    
    const currentPosition = entryToMove.position;
    if (currentPosition === newPosition) {
      return; // No change needed
    }
    
    // Calculate which entries need to be shifted
    const entriesToUpdate: { id: string; newPosition: number }[] = [];
    
    if (newPosition < currentPosition) {
      // Moving UP (e.g., 3 → 1): shift entries between newPosition and currentPosition-1 down
      for (const entry of waitingEntries) {
        if (entry.id === entryId) {
          entriesToUpdate.push({ id: entry.id, newPosition });
        } else if (entry.position >= newPosition && entry.position < currentPosition) {
          entriesToUpdate.push({ id: entry.id, newPosition: entry.position + 1 });
        }
      }
    } else {
      // Moving DOWN (e.g., 1 → 3): shift entries between currentPosition+1 and newPosition up
      for (const entry of waitingEntries) {
        if (entry.id === entryId) {
          entriesToUpdate.push({ id: entry.id, newPosition });
        } else if (entry.position > currentPosition && entry.position <= newPosition) {
          entriesToUpdate.push({ id: entry.id, newPosition: entry.position - 1 });
        }
      }
    }
    
    // Apply all updates in the transaction
    for (const update of entriesToUpdate) {
      const entryRef = doc(waitlistRef, update.id);
      transaction.update(entryRef, { position: update.newPosition });
    }
  });
};

// User operations
export const getUserProfile = async (db: Firestore, userId: string): Promise<UserProfile | null> => {
  const userRef = doc(db, 'users', userId);
  
  try {
    const snapshot = await getDoc(userRef);
    if (!snapshot.exists()) {
      return null;
    }
    
    const profileData = { uid: snapshot.id, ...snapshot.data() } as UserProfile;
    return profileData;
  } catch (error) {
    throw error;
  }
};

export const createUserProfile = async (
  db: Firestore,
  userId: string,
  data: CreateUserProfileData
): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  
  try {
    // Filter out undefined values before saving to Firestore
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== undefined)
    );
    
    // Use setDoc instead of createWithTimestamps for user profiles
    await setDoc(userRef, {
      ...filteredData,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
  } catch (error) {
    throw error;
  }
};

export const updateUserProfile = async (
  db: Firestore,
  userId: string,
  data: UpdateUserProfileData
): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  
  try {
    await updateWithTimestamp(userRef, data);
  } catch (error) {
    throw error;
  }
};

// Real-time listeners
export const subscribeToRooms = (
  db: Firestore,
  callback: (rooms: Room[]) => void
): Unsubscribe => {
  const roomsRef = collection(db, 'rooms');
  const q = query(roomsRef);
  return onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
    const rooms = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as Room));
    callback(rooms);
  });
};

export const subscribeToTables = (
  db: Firestore,
  roomId: string,
  callback: (tables: Table[]) => void
): Unsubscribe => {
  const tablesRef = collection(db, 'rooms', roomId, 'tables');
  const q = query(tablesRef);
  return onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
    const tables = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as Table));
    callback(tables);
  });
};

export const subscribeToWaitlist = (
  db: Firestore,
  roomId: string,
  tableId: string,
  callback: (waitlist: WaitlistEntry[]) => void
): Unsubscribe => {
  try {
    // Try to get a fresh Firebase instance if available
    let firestoreDb = db;
    if (typeof window !== 'undefined' && (window as any).getFirestoreDb) {
      try {
        firestoreDb = (window as any).getFirestoreDb();
      } catch (err) {
        // Use original db if fresh instance fails
      }
    }
    
    const waitlistRef = collection(firestoreDb, 'rooms', roomId, 'tables', tableId, 'waitlist');
    
    // Try position query first, fallback to added_at
    let q = query(waitlistRef, orderBy('position', 'asc'));
    
    try {
      const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
        const waitlist = snapshot.docs.map((doc: any) => {
          const data = doc.data();
          // Ensure position field exists, default to 0 if missing
          if (data.position === undefined) {
            data.position = 0;
          }
          return { id: doc.id, ...data } as WaitlistEntry;
        });
        
        callback(waitlist);
      }, (_error) => {
        // If position query fails, try added_at query
        const fallbackQuery = query(waitlistRef, orderBy('added_at', 'asc'));
        return onSnapshot(fallbackQuery, (snapshot: QuerySnapshot<DocumentData>) => {
          const waitlist = snapshot.docs.map((doc: any) => {
            const data = doc.data();
            // Ensure position field exists, default to 0 if missing
            if (data.position === undefined) {
              data.position = 0;
            }
            return { id: doc.id, ...data } as WaitlistEntry;
          });
          callback(waitlist);
        }, () => {
          // Log error for debugging but don't expose to user
        });
      });
      
      return unsubscribe;
    } catch (error) {
      callback([]);
      return () => {};
    }
  } catch (error) {
    callback([]);
    return () => {};
  }
};

// Batch operations
export const batchUpdateTables = async (
  db: Firestore,
  roomId: string,
  updates: Array<{ tableId: string; data: UpdateTableData }>
): Promise<void> => {
  const batch = writeBatch(db);
  
  updates.forEach(({ tableId, data }) => {
    const tableRef = doc(db, 'rooms', roomId, 'tables', tableId);
    batch.update(tableRef, {
      ...data,
      updated_at: serverTimestamp(),
    });
  });
  
  await batch.commit();
};

// Query helpers
export const getTablesByStatus = async (
  db: Firestore,
  roomId: string,
  status: string
): Promise<Table[]> => {
  const tablesRef = collection(db, 'rooms', roomId, 'tables');
  const q = query(tablesRef, where('status', '==', status));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as Table));
};

export const getWaitlistByStatus = async (
  db: Firestore,
  roomId: string,
  tableId: string,
  status: string
): Promise<WaitlistEntry[]> => {
  const waitlistRef = collection(db, 'rooms', roomId, 'tables', tableId, 'waitlist');
  const q = query(waitlistRef, where('status', '==', status), orderBy('position', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as WaitlistEntry));
};

// Helper function to check if a poker alias is unique
export const isAliasUnique = async (
  db: Firestore,
  alias: string,
  excludeUserId?: string
): Promise<boolean> => {
  if (!alias || alias.trim() === '') return true; // Empty aliases are allowed
  
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('alias', '==', alias.trim()));
  const querySnapshot = await getDocs(q);
  
  // If no documents found, alias is unique
  if (querySnapshot.empty) return true;
  
  // If excludeUserId is provided, check if the only match is the current user
  if (excludeUserId) {
    const matches = querySnapshot.docs.filter(doc => doc.id !== excludeUserId);
    return matches.length === 0;
  }
  
  return false;
};

// Helper function to get display name for waitlist entries
export const getWaitlistDisplayName = async (
  db: Firestore,
  entry: WaitlistEntry
): Promise<string> => {
  // If entry has user_id, try to get the user's alias or name
  if (entry.user_id) {
    try {
      const userProfile = await getUserProfile(db, entry.user_id);
      if (userProfile) {
        // Use alias if available, otherwise use name
        return userProfile.alias || userProfile.name;
      }
    } catch (error) {
      // Silently handle user profile fetch failure for waitlist display
    }
  }

  // Fallback to the entry's name field
  return entry.name;
};

// Batch helper to get display names for multiple waitlist entries
export const getWaitlistDisplayNames = async (
  db: Firestore,
  entries: WaitlistEntry[]
): Promise<Array<WaitlistEntry & { displayName: string }>> => {
  const entriesWithDisplayNames = await Promise.all(
    entries.map(async (entry) => {
      const displayName = await getWaitlistDisplayName(db, entry);
      return { ...entry, displayName };
    })
  );
  
  return entriesWithDisplayNames;
};

// Push token management functions
export const addUserPushToken = async (
  db: Firestore,
  userId: string,
  pushToken: string
): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    pushTokens: arrayUnion(pushToken),
    last_active: serverTimestamp(),
  });
};

export const removeUserPushToken = async (
  db: Firestore,
  userId: string,
  pushToken: string
): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    pushTokens: arrayRemove(pushToken),
  });
};

export const removeAllUserPushTokens = async (
  db: Firestore,
  userId: string
): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, { pushTokens: [] });
};